import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useEffect, useCallback } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { assessmentAnswerText } from './assessmentAnswerUtils';
import {
  canonicalizeAdjacentRoles,
  canonicalizeDepartment,
  canonicalizeIndustries,
  canonicalizeLeadershipReadiness,
  canonicalizeRoleList
} from '../../utils/talentPoolTaxonomy';

const ImportedApplicantDetailsPopup = ({
  applicant,
  onClose,
  onUpdate,
  onRequestReprocess
}) => {
  useUiLanguage();
  const { currentUser } = useAuth();
  const { t } = useTranslation('applicants');
  const { isRTL, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('profile'); // profile, intelligence, history, analysis
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [companyUID, setCompanyUID] = useState('');

  useEffect(() => {
    const resolveCompany = async () => {
      if (!currentUser) return;
      try {
        const ownerSnap = await get(ref(database, `companies/${currentUser.uid}`));
        if (ownerSnap.exists()) {
          setCompanyUID(currentUser.uid);
          return;
        }
        const collabSnap = await get(ref(database, `collaborators/${currentUser.uid}`));
        if (collabSnap.exists()) {
          setCompanyUID(collabSnap.val().companyId);
        }
      } catch (e) {
        console.error('Resolve company failed:', e);
      }
    };
    resolveCompany();
  }, [currentUser]);

  const loadDetails = useCallback(async (uid) => {
    const resolvedUid = uid || companyUID;
    if (!resolvedUid) return;
    try {
      setLoadingHistory(true);
      // Load the profile-owned parsing payload so the popup reflects the same
      // normalized record used by the talent-pool analytics screens.
      // Try new structure first: imported_profiles/{employerUID}/{applicantId}
      let profileRef = ref(database, `imported_profiles/${resolvedUid}/${applicant.applicantId}`);
      let snapshot = await get(profileRef);

      // Fallback to old structure if not found
      if (!snapshot.exists()) {
        profileRef = ref(database, `imported_profiles/${applicant.applicantId}`);
        snapshot = await get(profileRef);
      }

      if (snapshot.exists()) {
        const data = snapshot.val();

        // Load History
        if (data.history) {
          const historyList = Object.values(data.history).sort((a, b) => b.archivedAt - a.archivedAt);
          setHistory(historyList);
        } else {
          setHistory([]);
        }

        setParsedData(data.parsedData || null);
        setExtraction(data.extraction || null);
        setAnalytics(data.analytics || null);
        setProcessing(data.processing || null);

        // Load Analysis (try to find latest from history or profile)
        if (data.applicantAnalysis) {
          setAnalysis(data.applicantAnalysis);
        } else if (data.history) {
          // Find latest analysis in history
          const sortedHistory = Object.values(data.history).sort((a, b) => b.archivedAt - a.archivedAt);
          const latestWithAnalysis = sortedHistory.find(h => h.applicantAnalysis);
          if (latestWithAnalysis) {
            setAnalysis(latestWithAnalysis.applicantAnalysis);
          } else {
            setAnalysis(null);
          }
        } else {
          setAnalysis(null);
        }
      } else {
        setHistory([]);
        setParsedData(null);
        setExtraction(null);
        setAnalytics(null);
        setProcessing(null);
        setAnalysis(null);
      }
    } catch (error) {
      console.error("Error loading details:", error);
    } finally {
      setLoadingHistory(false);
    }
  }, [applicant, companyUID]);

  useEffect(() => {
    if (applicant && applicant.applicantId && companyUID) {
      loadDetails(companyUID);
    }
  }, [applicant, companyUID, loadDetails]);

  const locale = language === 'ar' ? 'ar-EG' : 'en-US';

  const formatDate = (timestamp) => {
    if (!timestamp) return t('importedDetails.common.notAvailable');
    return new Date(timestamp).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t('importedDetails.common.notAvailable');
    return new Date(timestamp).toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Matches colors from MyTeam.js
  const COLORS = {
    purple: '#CC40EB',
    yellow: '#EBDD40',
    orange: '#EB8D40',
    blue: '#568ded',
    indigo: '#4048EB',
    green: '#40EB6E',
    red: '#EB4040',
    grey: '#888888'
  };

  const IntelligencePanel = ({ title, subtitle, children, accent = false }) => (
    <div style={{
      background: accent ? 'linear-gradient(180deg, #f7fbff 0%, #ffffff 100%)' : '#fff',
      border: accent ? "1px solid var(--app-border, rgba(86, 141, 237, 0.18))" : '1px solid #edf1f6',
      borderRadius: '18px',
      padding: '18px 20px'
    }}>
      <div style={{ marginBottom: '14px' }}>
        <h4 style={{ margin: 0, color: '#23314d', fontSize: '16px' }}>{title}</h4>
        {subtitle ? <p style={{ margin: '6px 0 0 0', color: '#6b778c', fontSize: '13px', lineHeight: '1.6' }}>{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );

  const toggleSection = (historyIndex, sectionName) => {
    const key = `${historyIndex}_${sectionName}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isSectionExpanded = (historyIndex, sectionName) => {
    const key = `${historyIndex}_${sectionName}`;
    return expandedSections[key] || false;
  };

  // Render formatted analysis with styled headers
  const renderFormattedAnalysis = (text) => {
    if (!text) return null;

    const translatedHeaders = Object.values(t('importedDetails.analysis.sections', { returnObjects: true })) || [];
    const sectionHeaders = Array.from(new Set([
      'Summary of Experience:',
      'Strong Points:',
      'Weak Points:',
      'Global Standards Assessment:',
      'Verdict:',
      'Interview Questions:',
      ...translatedHeaders
    ]));

    const lines = text.split('\n');

    return lines.map((line, index) => {
      const isHeader = sectionHeaders.some(header => line.trim() === header);

      if (isHeader) {
        return (
          <div key={index} style={{
            fontWeight: "var(--app-weight-strong, bold)",
            fontSize: '15px',
            color: COLORS.blue,
            marginTop: index === 0 ? '0' : '15px',
            marginBottom: '6px',
            borderBottom: `1px solid ${COLORS.blue}`,
            paddingBottom: '3px'
          }}>
            {line}
          </div>
        );
      }

      return <div key={index} style={{ marginBottom: '2px', lineHeight: '1.5' }}>{line}</div>;
    });
  };

  // Get rating color based on value
  const getRatingColor = (rating) => {
    const ratingColors = {
      'Excellent': COLORS.green,
      'Good': COLORS.blue,
      'Average': COLORS.yellow,
      'Below Average': COLORS.orange,
      'Poor': COLORS.red
    };
    return ratingColors[rating] || COLORS.grey;
  };

  const translateRating = (rating) => {
    if (!rating) return t('importedDetails.common.notAvailable');
    const key = rating.replace(/\s+/g, '');
    return t(`ratings.${key}`, rating);
  };

  // Get interview status info
  const getInterviewStatus = (interview) => {
    if (interview.status === 'cancelled') return { text: t('importedDetails.interviews.status.cancelled'), color: COLORS.red, icon: 'fa-ban' };
    if (interview.status === 'completed') return { text: t('importedDetails.interviews.status.completed'), color: COLORS.green, icon: 'fa-check-circle' };

    const now = new Date();
    const interviewDate = new Date(`${interview.date}T${interview.time}`);

    if (interviewDate < now) {
      return { text: t('importedDetails.interviews.status.completed'), color: COLORS.green, icon: 'fa-check-circle' };
    } else {
      return { text: t('importedDetails.interviews.status.scheduled'), color: COLORS.blue, icon: 'fa-calendar-check' };
    }
  };

  const resolvedParsedData = parsedData || applicant?.parsedData || null;
  const resolvedExtraction = extraction || applicant?.extraction || null;
  const resolvedAnalytics = analytics || applicant?.analytics || null;
  const resolvedProcessing = processing || applicant?.processing || null;
  const extractedExperience = resolvedExtraction?.experience || {};
  const extractedSkills = resolvedExtraction?.skills || {};
  const extractedSignals = resolvedExtraction?.signals || {};
  const extractedHeadline = resolvedExtraction?.professionalHeadline || {};
  const extractedInsights = resolvedExtraction?.hrInsights || {};
  const positionFamily = resolvedAnalytics?.positionFamily || resolvedExtraction?.marketProfile?.positionFamily || '';
  const currentTitle = extractedHeadline?.currentTitle || resolvedExtraction?.experience?.primaryPosition || resolvedParsedData?.currentTitle || '';
  const topRoleArchetypes = canonicalizeRoleList(
    Array.isArray(resolvedExtraction?.fit?.roleArchetypes) ? resolvedExtraction.fit.roleArchetypes : (applicant?.topRoleArchetypes || []),
    { positionFamily, fallback: [currentTitle] }
  );
  const adjacentRoles = canonicalizeAdjacentRoles(
    resolvedAnalytics?.adjacentRoles || resolvedExtraction?.fit?.adjacentRoles || [],
    {
      positionFamily,
      primaryRole: currentTitle
    }
  );
  const department = resolvedAnalytics?.department || resolvedExtraction?.marketProfile?.department || canonicalizeDepartment(
    resolvedExtraction?.marketProfile?.primaryFunction || '',
    {
      positionFamily,
      title: currentTitle,
      skills: resolvedExtraction?.skills?.normalized || resolvedParsedData?.skills || []
    }
  );
  const missingRequiredFields = Array.isArray(resolvedAnalytics?.missingRequiredFields) ? resolvedAnalytics.missingRequiredFields : (applicant?.missingRequiredFields || []);
  const lowConfidenceFields = Array.isArray(resolvedAnalytics?.lowConfidenceFields) ? resolvedAnalytics.lowConfidenceFields : (applicant?.lowConfidenceFields || []);
  const recruiterSummary = resolvedAnalytics?.recruiterSummary || resolvedExtraction?.hrInsights?.recruiterSummary || applicant?.recruiterSummary || applicant?.summary || '';
  const leadershipReadiness = canonicalizeLeadershipReadiness(
    resolvedAnalytics?.leadershipReadiness || resolvedExtraction?.fit?.leadershipReadiness || '',
    {
      seniorityBucket: resolvedAnalytics?.seniorityBucket || resolvedExtraction?.experience?.seniority || '',
      title: currentTitle,
      years: Number(resolvedExtraction?.experience?.totalYears || resolvedParsedData?.totalYearsExperience || 0),
      peopleManagementScope: resolvedExtraction?.experience?.peopleManagementScope || '',
      leadershipScope: resolvedExtraction?.experience?.leadershipScope || ''
    }
  );
  const normalizedSkills = Array.isArray(resolvedExtraction?.skills?.normalized)
    ? resolvedExtraction.skills.normalized
    : (Array.isArray(resolvedParsedData?.skills) ? resolvedParsedData.skills : []);
  const languages = Array.isArray(resolvedExtraction?.skills?.languages)
    ? resolvedExtraction.skills.languages
    : (Array.isArray(resolvedParsedData?.languages) ? resolvedParsedData.languages : []);
  const targetRoles = canonicalizeRoleList(
    Array.isArray(extractedHeadline?.targetRoles) ? extractedHeadline.targetRoles : (Array.isArray(resolvedParsedData?.targetRoles) ? resolvedParsedData.targetRoles : []),
    { positionFamily, fallback: [resolvedExtraction?.experience?.primaryPosition || resolvedParsedData?.currentTitle || ''] }
  );
  const certifications = Array.isArray(extractedSkills?.certifications)
    ? extractedSkills.certifications
    : (Array.isArray(resolvedParsedData?.certifications) ? resolvedParsedData.certifications : []);
  const methodologies = Array.isArray(extractedSkills?.methodologies) ? extractedSkills.methodologies : [];
  const domainExposure = canonicalizeIndustries(
    extractedExperience?.domainExposure
    || resolvedExtraction?.marketProfile?.industries
    || resolvedParsedData?.industries
    || []
  );
  const educationItems = Array.isArray(resolvedExtraction?.education)
    ? resolvedExtraction.education
    : (Array.isArray(resolvedParsedData?.education) ? resolvedParsedData.education : []);
  const currentCompany = extractedExperience?.currentCompany || '';
  const peopleManagementScope = extractedExperience?.peopleManagementScope || '';
  const leadershipScope = extractedExperience?.leadershipScope || '';
  const budgetOwnership = extractedExperience?.budgetOwnership || '';
  const promotionPattern = extractedExperience?.promotionPattern || extractedSignals?.promotionPattern || '';
  const stabilitySignal = extractedExperience?.stabilitySignal || extractedSignals?.careerStability || '';
  const workModePreference = extractedSignals?.workModePreference || '';
  const profileShape = resolvedExtraction?.fit?.profileShape || '';
  const employmentGaps = Array.isArray(extractedExperience?.employmentGaps) ? extractedExperience.employmentGaps : [];
  const recommendedNextActions = Array.isArray(extractedInsights?.recommendedNextActions) ? extractedInsights.recommendedNextActions : [];
  const missingContactData = Array.isArray(extractedInsights?.missingContactData) ? extractedInsights.missingContactData : [];
  const missingEvaluationData = Array.isArray(extractedInsights?.missingEvaluationData) ? extractedInsights.missingEvaluationData : [];
  const recruiterEvidenceItems = [
    { label: t('importedDetails.intelligence.activeRoles', 'Active roles'), value: Number(applicant?.activeConnectionsCount || 0) },
    { label: t('importedDetails.intelligence.interviews', 'Interviews'), value: Number(applicant?.interviewCount || 0) },
    { label: t('importedDetails.intelligence.notes', 'Notes'), value: Number(applicant?.notesCount || 0) },
    { label: t('importedDetails.intelligence.messages', 'Messages'), value: Number(applicant?.messagesCount || 0) }
  ].filter((item) => item.value > 0);

  const handleReprocess = async () => {
    if (!onRequestReprocess || !applicant?.applicantId) return;
    setReprocessing(true);
    try {
      await onRequestReprocess(applicant, { source: 'manual_reprocess', force: true });
      await loadDetails(companyUID);
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error('Manual talent-pool reprocess failed:', error);
    } finally {
      setReprocessing(false);
    }
  };

  // Collapsible Section Header Component - Updated to flat design
  const SectionHeader = ({ title, count, icon, isExpanded, onClick, color = COLORS.blue }) => (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 0', // Removed horizontal padding for cleaner look
        borderBottom: isExpanded ? 'none' : '1px solid #f0f0f0',
        cursor: 'pointer',
        marginTop: '5px',
        transition: 'all 0.2s ease',
        userSelect: 'none'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          backgroundColor: `${color}1A`, // 10% opacity
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <i className={`fa-solid ${icon}`} style={{ color: color, fontSize: '14px' }}></i>
        </div>
        <span style={{ fontWeight: '600', fontSize: '15px', color: "var(--app-neutral-ink, #333)" }}>{title}</span>
        {count !== undefined && (
          <span style={{
            backgroundColor: `${color}26`,
            color: color,
            fontSize: '11px',
            padding: '2px 8px',
            borderRadius: '12px',
            fontWeight: '600'
          }}>
            {count}
          </span>
        )}
      </div>
      <i className={`fa-solid ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} style={{ color: 'var(--app-text, #ccc)', fontSize: '12px' }}></i>
    </div>
  );

  // Section Content Wrapper - Cleaner flat design
  const SectionContent = ({ isExpanded, children }) => {
    if (!isExpanded) return null;
    return (
      <div style={{
        padding: '15px 0 25px 38px', // Indent to align with text start
        borderBottom: '1px solid #f0f0f0',
        animation: 'fadeIn 0.2s ease-in-out'
      }}>
        {children}
      </div>
    );
  };

  const renderHistoryItem = (item, index) => {
    const notesArray = item.notes ? Object.values(item.notes) : [];
    const scoresArray = item.scores ? Object.entries(item.scores) : [];
    const messagesArray = item.messages ? Object.values(item.messages).sort((a, b) => a.timestamp - b.timestamp) : [];
    const assessmentsObj = item.assessments || {};
    const interviewsArray = item.interviews ? Object.values(item.interviews) : [];

    // Check if empty
    const isEmpty = !item.applicantAnalysis &&
      Object.keys(assessmentsObj).length === 0 &&
      scoresArray.length === 0 &&
      notesArray.length === 0 &&
      interviewsArray.length === 0 &&
      messagesArray.length === 0;

    return (
      <div key={index} style={{
        backgroundColor: '#fff',
        // Removed heavy border and shadow for flat design
        borderBottom: '1px solid #eee',
        marginBottom: '30px',
        paddingBottom: '20px'
      }}>
        {/* Header - Simplified */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px'
        }}>
          <div>
            <h4 style={{ margin: '0 0 6px 0', color: "var(--app-neutral-ink, #333)", fontSize: '18px', fontWeight: "var(--app-weight-strong, 700)" }}>{item.jobTitle}</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: '#888' }}>
                <i className="fa-solid fa-calendar" style={{ marginRight: '5px' }}></i>
                {formatDate(item.archivedAt)}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--app-text, #ccc)' }}>•</span>
              <span style={{ fontSize: '12px', color: '#888' }}>
                {item.stageName || item.substageId?.replace(/-/g, ' ') || t('importedDetails.history.unknownStage')}
              </span>
            </div>
          </div>
          <div>
            <div style={{
              fontSize: '13px',
              fontWeight: "var(--app-weight-strong, 700)",
              color: item.isHired ? COLORS.green : COLORS.grey,
              backgroundColor: item.isHired ? `${COLORS.green}1A` : '#f5f5f5',
              padding: '6px 12px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <i className={`fa-solid ${item.isHired ? 'fa-trophy' : 'fa-circle-xmark'}`}></i>
              {item.isHired ? t('importedDetails.statusBadge.hired') : t('importedDetails.statusBadge.notHired')}
            </div>
          </div>
        </div>

        {/* Expandable Sections Container */}
        <div>

          {/* AI Analysis Section */}
          {item.applicantAnalysis && (
            <>
              <SectionHeader
                title={t('importedDetails.sections.analysis')}
                icon="fa-robot"
                isExpanded={isSectionExpanded(index, 'analysis')}
                onClick={() => toggleSection(index, 'analysis')}
                color={COLORS.purple} // User requested purple
              />
              <SectionContent isExpanded={isSectionExpanded(index, 'analysis')}>
                <div style={{ fontSize: '14px', color: "var(--app-neutral-ink, #333)", lineHeight: '1.6' }}>
                  {renderFormattedAnalysis(item.applicantAnalysis)}
                </div>
              </SectionContent>
            </>
          )}

          {/* Assessment Results Section */}
          {Object.keys(assessmentsObj).length > 0 && (
            <>
              <SectionHeader
                title={t('importedDetails.sections.assessments')}
                count={Object.keys(assessmentsObj).length}
                icon="fa-clipboard-check"
                isExpanded={isSectionExpanded(index, 'assessments')}
                onClick={() => toggleSection(index, 'assessments')}
                color={COLORS.blue}
              />
              <SectionContent isExpanded={isSectionExpanded(index, 'assessments')}>
                {Object.entries(assessmentsObj).map(([assessmentId, assessment], aIdx) => (
                  <div key={aIdx} style={{
                    marginBottom: '15px',
                    padding: '15px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '8px', color: "var(--app-neutral-ink, #333)" }}>
                      {t('importedDetails.sections.assessmentLabel', { id: assessmentId })}
                    </div>

                    {/* Evaluation Summary */}
                    {assessment.evaluation && (
                      <div style={{ backgroundColor: '#f0fff4', padding: '10px', borderRadius: '6px', marginBottom: '10px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.green, marginBottom: '5px' }}>
                          <i className="fa-solid fa-star" style={{ marginRight: '5px' }}></i>
                          {t('importedDetails.assessment.score')}: {assessment.evaluation.overallScore || assessment.evaluation.score || t('importedDetails.common.notAvailable')}
                        </div>
                        {assessment.evaluation.recommendation && (
                          <div style={{ fontSize: '12px', color: '#555' }}>
                            <strong>{t('importedDetails.assessment.recommendation')}:</strong> {assessment.evaluation.recommendation}
                          </div>
                        )}
                        {assessment.evaluation.strengths && (
                          <div style={{ fontSize: '12px', color: '#555', marginTop: '5px' }}>
                            <strong>{t('importedDetails.assessment.strengths')}:</strong> {Array.isArray(assessment.evaluation.strengths) ? assessment.evaluation.strengths.join(', ') : assessment.evaluation.strengths}
                          </div>
                        )}
                        {assessment.evaluation.areasForImprovement && (
                          <div style={{ fontSize: '12px', color: '#555', marginTop: '5px' }}>
                            <strong>{t('importedDetails.assessment.improvements')}:</strong> {Array.isArray(assessment.evaluation.areasForImprovement) ? assessment.evaluation.areasForImprovement.join(', ') : assessment.evaluation.areasForImprovement}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Q&A Breakdown */}
                    {assessment.response && assessment.response.answers && (
                      <div style={{ marginTop: '8px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '6px' }}>
                          {t('importedDetails.assessment.responseDetails')}
                        </div>
                        {Object.entries(assessment.response.answers).map(([qId, answer], qIdx) => (
                          <div key={qIdx} style={{
                            backgroundColor: '#f8f9fa',
                            padding: '8px 10px',
                            borderRadius: '4px',
                            marginBottom: '6px',
                            fontSize: '12px'
                          }}>
                            <div style={{ color: '#666', marginBottom: '4px' }}>
                              <strong>{t('importedDetails.assessment.question', { number: qIdx + 1 })}</strong> {assessment.response.questions?.[qId] || `Q${qIdx + 1}`}
                            </div>
                            <div style={{ color: "var(--app-neutral-ink, #333)" }}>
                              <strong>{t('importedDetails.assessment.answer')}:</strong>{' '}
                              {assessmentAnswerText(answer, t('importedDetails.common.notAvailable'))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </SectionContent>
            </>
          )}

          {/* Team Scorecards Section */}
          {scoresArray.length > 0 && (
            <>
              <SectionHeader
                title={t('importedDetails.sections.teamScorecards')}
                count={scoresArray.length}
                icon="fa-star"
                isExpanded={isSectionExpanded(index, 'scores')}
                onClick={() => toggleSection(index, 'scores')}
                color={COLORS.yellow} // User requested yellow
              />
              <SectionContent isExpanded={isSectionExpanded(index, 'scores')}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {scoresArray.map(([scorerId, scoreData], sIdx) => (
                    <div key={sIdx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: COLORS.blue,
                          color: 'var(--app-text, #fff)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}>
                          {(scoreData.userEmail || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: "var(--app-neutral-ink, #333)" }}>
                            {scoreData.userEmail?.split('@')[0] || t('importedDetails.scorecards.teamMember')}
                          </div>
                          {scoreData.updatedAt && (
                            <div style={{ fontSize: '11px', color: '#888' }}>
                              {formatDate(scoreData.updatedAt)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{
                        padding: '4px 12px',
                        borderRadius: '15px',
                        backgroundColor: getRatingColor(scoreData.rating),
                        color: 'var(--app-text, #fff)',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {translateRating(scoreData.rating)}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionContent>
            </>
          )}

          {/* Team Notes Section */}
          {notesArray.length > 0 && (
            <>
              <SectionHeader
                title={t('importedDetails.sections.teamNotes')}
                count={notesArray.length}
                icon="fa-sticky-note"
                isExpanded={isSectionExpanded(index, 'notes')}
                onClick={() => toggleSection(index, 'notes')}
                color={COLORS.indigo} // User requested distinct color
              />
              <SectionContent isExpanded={isSectionExpanded(index, 'notes')}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {notesArray.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)).map((note, nIdx) => (
                    <div key={nIdx} style={{
                      padding: '12px',
                      backgroundColor: '#f8fbfc',
                      borderRadius: '8px',
                      borderLeft: `3px solid ${COLORS.indigo}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.indigo }}>
                          <i className="fa-solid fa-user" style={{ marginRight: '5px' }}></i>
                          {note.user?.split('@')[0] || t('importedDetails.notes.author')}
                        </span>
                        {note.timestamp && (
                          <span style={{ fontSize: '11px', color: '#888' }}>
                            {formatDateTime(note.timestamp)}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', color: "var(--app-neutral-ink, #333)", lineHeight: '1.5' }}>
                        {note.content}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionContent>
            </>
          )}

          {/* Interview History Section */}
          {interviewsArray.length > 0 && (
            <>
              <SectionHeader
                title={t('importedDetails.sections.interviewHistory')}
                count={interviewsArray.length}
                icon="fa-calendar-days"
                isExpanded={isSectionExpanded(index, 'interviews')}
                onClick={() => toggleSection(index, 'interviews')}
                color={COLORS.blue}
              />
              <SectionContent isExpanded={isSectionExpanded(index, 'interviews')}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {interviewsArray.sort((a, b) => new Date(b.date) - new Date(a.date)).map((interview, iIdx) => {
                    const status = getInterviewStatus(interview);
                    return (
                      <div key={iIdx} style={{
                        padding: '12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #eee'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: "var(--app-neutral-ink, #333)", marginBottom: '4px' }}>
                              {interview.title || interview.type || t('importedDetails.interviews.titleFallback')}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              <i className="fa-solid fa-clock" style={{ marginRight: '5px' }}></i>
                              {interview.date}{uiText(" at ")}{interview.time}
                              {interview.duration && uiText(" ({{count}} min)", { count: interview.duration })}
                            </div>
                          </div>
                          <span style={{
                            color: status.color,
                            fontSize: '12px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}>
                            <i className={`fa-solid ${status.icon}`}></i>
                            {status.text}
                          </span>
                        </div>

                        {interview.location && (
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                            <i className="fa-solid fa-location-dot" style={{ marginRight: '5px' }}></i>
                            {interview.location}
                          </div>
                        )}

                        {interview.interviewerEmail && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            <i className="fa-solid fa-user-tie" style={{ marginRight: '5px' }}></i>
                            {t('importedDetails.interviews.interviewer', { name: interview.interviewerEmail?.split('@')[0] })}
                          </div>
                        )}

                        {interview.notes && (
                          <div style={{ fontSize: '12px', color: '#555', marginTop: '8px', fontStyle: 'italic', backgroundColor: '#fff', padding: '8px', borderRadius: '4px' }}>
                            {interview.notes}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </SectionContent>
            </>
          )}

          {/* Messages Section */}
          {messagesArray.length > 0 && (
            <>
              <SectionHeader
                title={t('importedDetails.sections.messages')}
                count={messagesArray.length}
                icon="fa-comments"
                isExpanded={isSectionExpanded(index, 'messages')}
                onClick={() => toggleSection(index, 'messages')}
                color={COLORS.purple}
              />
              <SectionContent isExpanded={isSectionExpanded(index, 'messages')}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {messagesArray.map((msg, mIdx) => {
                    const isEmployer = msg.user && !msg.user.includes('@');
                    return (
                      <div key={mIdx} style={{
                        display: 'flex',
                        justifyContent: isEmployer ? 'flex-end' : 'flex-start'
                      }}>
                        <div style={{
                          maxWidth: '80%',
                          padding: '10px 12px',
                          backgroundColor: isEmployer ? COLORS.blue : '#f1f1f1',
                          color: isEmployer ? 'var(--app-text, #fff)' : '#333',
                          borderRadius: '12px',
                          fontSize: '13px'
                        }}>
                          <div style={{ marginBottom: '4px', lineHeight: '1.4' }}>
                            {msg.message}
                          </div>
                          <div style={{
                            fontSize: '10px',
                            opacity: 0.7,
                            textAlign: isEmployer ? 'right' : 'left'
                          }}>
                            {formatDateTime(msg.timestamp)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </SectionContent>
            </>
          )}

          {/* No Data Message - Vertically Centered Flexbox */}
          {isEmpty && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              color: '#888',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              marginTop: '10px',
              border: '1px dashed #e0e0e0'
            }}>
              <i className="fa-solid fa-inbox" style={{ fontSize: '24px', marginBottom: '15px', color: 'var(--app-text, #ccc)' }}></i>
              <span style={{ fontSize: '14px', fontWeight: '500' }}>{t('importedDetails.history.noData')}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="applicant-details-overlay" style={{ display: 'flex', direction: isRTL ? 'rtl' : 'ltr', fontFamily: isRTL ? 'Alexandria, Montserrat, sans-serif' : 'Montserrat, sans-serif' }}>
      <div className="applicant-details-modal" style={{ position: 'relative', width: '90%', maxWidth: '1000px', height: '85vh' }}>
        <div className="close" onClick={onClose} style={{ position: 'absolute', top: '15px', [isRTL ? 'left' : 'right']: '15px', zIndex: 10, fontSize: '24px', cursor: 'pointer' }}>&times;</div>

        <div style={{ display: 'flex', height: '100%', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          {/* Sidebar / Header Area */}
          <div style={{ width: '300px', backgroundColor: '#f8f9fa', [isRTL ? 'borderLeft' : 'borderRight']: '1px solid #dee2e6', padding: '20px', display: 'flex', flexDirection: 'column', textAlign: isRTL ? 'right' : 'left' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{
                width: '80px', height: '80px', backgroundColor: '#568ded', color: '#fff',
                borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '32px', fontWeight: "var(--app-weight-strong, bold)", margin: '0 auto 15px auto'
              }}>
                {(applicant?.name || 'U').charAt(0).toUpperCase()}
              </div>
              <h2 style={{ margin: '0 0 5px 0', fontSize: '20px', color: "var(--app-neutral-ink, #333)" }}>{applicant?.name || t('importedDetails.history.unknownJobTitle')}</h2>
              <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>{applicant?.email}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginTop: '12px' }}>
                {resolvedAnalytics?.positionFamily && (
                  <span style={{ backgroundColor: '#f1f4f8', color: '#445166', fontSize: '11px', fontWeight: "var(--app-weight-strong, 700)", padding: '5px 10px', borderRadius: '999px' }}>
                    {resolvedAnalytics.positionFamily}
                  </span>
                )}
                {resolvedAnalytics?.seniorityBucket && (
                  <span style={{ backgroundColor: '#f8f1ff', color: COLORS.purple, fontSize: '11px', fontWeight: "var(--app-weight-strong, 700)", padding: '5px 10px', borderRadius: '999px' }}>
                    {resolvedAnalytics.seniorityBucket}
                  </span>
                )}
              </div>
            </div>

            <div style={{ marginTop: '20px' }}>
              <button
                onClick={() => setActiveTab('profile')}
                style={{
                  display: 'block', width: '100%', padding: '10px 15px', textAlign: isRTL ? 'right' : 'left',
                  backgroundColor: activeTab === 'profile' ? '#e8f4fd' : 'transparent',
                  color: activeTab === 'profile' ? '#568ded' : '#333',
                  border: 'none', borderRadius: '6px', marginBottom: '5px', cursor: 'pointer',
                  fontWeight: activeTab === 'profile' ? '600' : '400'
                }}
                data-light-tab={activeTab === 'profile'}
                data-light-control={activeTab === 'profile' ? "quiet" : "quiet"}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <i className="fa-solid fa-user" style={{ marginRight: isRTL ? 0 : '10px', marginLeft: isRTL ? '10px' : 0, width: '20px', textAlign: 'center' }}></i>
                  {t('importedDetails.tabs.profile')}
                </span>
              </button>

              {(resolvedExtraction || resolvedAnalytics || resolvedProcessing) && (
                <button
                  onClick={() => setActiveTab('intelligence')}
                  style={{
                    display: 'block', width: '100%', padding: '10px 15px', textAlign: isRTL ? 'right' : 'left',
                    backgroundColor: activeTab === 'intelligence' ? '#e8f4fd' : 'transparent',
                    color: activeTab === 'intelligence' ? '#568ded' : '#333',
                    border: 'none', borderRadius: '6px', marginBottom: '5px', cursor: 'pointer',
                    fontWeight: activeTab === 'intelligence' ? '600' : '400'
                  }}
                  data-light-tab={activeTab === 'intelligence'}
                data-light-control={activeTab === 'intelligence' ? "quiet" : "quiet"}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <i className="fa-solid fa-brain" style={{ marginRight: isRTL ? 0 : '10px', marginLeft: isRTL ? '10px' : 0, width: '20px', textAlign: 'center' }}></i>
                    {t('importedDetails.tabs.intelligence', 'Intelligence')}
                  </span>
                </button>
              )}

              {analysis && (
                <button
                  onClick={() => setActiveTab('analysis')}
                  style={{
                    display: 'block', width: '100%', padding: '10px 15px', textAlign: isRTL ? 'right' : 'left',
                    backgroundColor: activeTab === 'analysis' ? '#e8f4fd' : 'transparent',
                    color: activeTab === 'analysis' ? '#568ded' : '#333',
                    border: 'none', borderRadius: '6px', marginBottom: '5px', cursor: 'pointer',
                    fontWeight: activeTab === 'analysis' ? '600' : '400'
                  }}
                  data-light-tab={activeTab === 'analysis'}
                data-light-control={activeTab === 'analysis' ? "quiet" : "quiet"}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <i className="fa-solid fa-chart-pie" style={{ marginRight: isRTL ? 0 : '10px', marginLeft: isRTL ? '10px' : 0, width: '20px', textAlign: 'center' }}></i>
                    {t('importedDetails.tabs.analysis')}
                  </span>
                </button>
              )}

              <button
                onClick={() => setActiveTab('history')}
                style={{
                  display: 'block', width: '100%', padding: '10px 15px', textAlign: isRTL ? 'right' : 'left',
                  backgroundColor: activeTab === 'history' ? '#e8f4fd' : 'transparent',
                  color: activeTab === 'history' ? '#568ded' : '#333',
                  border: 'none', borderRadius: '6px', marginBottom: '5px', cursor: 'pointer',
                  fontWeight: activeTab === 'history' ? '600' : '400'
                }}
                data-light-tab={activeTab === 'history'}
                data-light-control={activeTab === 'history' ? "quiet" : "quiet"}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <i className="fa-solid fa-clock-rotate-left" style={{ marginRight: isRTL ? 0 : '10px', marginLeft: isRTL ? '10px' : 0, width: '20px', textAlign: 'center' }}></i>
                  {t('importedDetails.tabs.history')}
                  {history.length > 0 && (
                    <span style={{ marginInlineStart: 'auto', backgroundColor: '#568ded', color: '#fff', fontSize: '10px', padding: '2px 6px', borderRadius: '10px' }}>{history.length}</span>
                  )}
                </span>
              </button>
            </div>

            <div style={{ marginTop: 'auto' }}>
              <button
                onClick={() => window.open(applicant?.cvLink || applicant?.cv?.Link || '#', '_blank')}
                style={{ '--light-control-fill': '#568ded',
                  display: 'block', width: '100%', padding: '10px',
                  backgroundColor: '#568ded', color: '#fff',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontWeight: '600', marginBottom: '10px'
                }}
                data-light-control={"filled"}>
                <i className="fa-solid fa-file-pdf" style={{ marginRight: isRTL ? 0 : '8px', marginLeft: isRTL ? '8px' : 0 }}></i>
                {t('importedDetails.cvButton')}
              </button>

              {onRequestReprocess && (
                <button
                  onClick={handleReprocess}
                  disabled={reprocessing || !(applicant?.cvLink || applicant?.cv?.Link)}
                  style={{
                    display: 'block', width: '100%', padding: '10px',
                    backgroundColor: '#fff', color: '#1f3a62',
                    border: '1px solid #d7e2f1', borderRadius: '6px', cursor: (reprocessing || !(applicant?.cvLink || applicant?.cv?.Link)) ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    opacity: reprocessing || !(applicant?.cvLink || applicant?.cv?.Link) ? 0.65 : 1
                  }}
                  data-light-control={"quiet"}>
                  <i className={`fa-solid ${reprocessing ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} style={{ marginRight: isRTL ? 0 : '8px', marginLeft: isRTL ? '8px' : 0 }}></i>
                  {reprocessing ? t('imported.reprocessing', 'Reprocessing') : t('imported.reprocessCv', 'Reprocess CV')}
                </button>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <div style={{ flex: 1, padding: '30px', overflowY: 'auto', textAlign: isRTL ? 'right' : 'left' }}>

            {activeTab === 'profile' && (
              <div>
                <h3 style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '10px', marginBottom: '20px', color: "var(--app-neutral-ink, #333)" }}>
                  {t('importedDetails.profile.title')}
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '4px' }}>{t('importedDetails.profile.email')}</label>
                    <div style={{ fontSize: '15px', color: "var(--app-neutral-ink, #333)" }}>{applicant?.email}</div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '4px' }}>{t('importedDetails.profile.phone')}</label>
                    <div style={{ fontSize: '15px', color: "var(--app-neutral-ink, #333)" }}>{applicant?.phone || t('importedDetails.profile.notProvided')}</div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '4px' }}>{t('importedDetails.profile.dateAdded')}</label>
                    <div style={{ fontSize: '15px', color: "var(--app-neutral-ink, #333)" }}>{formatDate(applicant?.createdAt)}</div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '4px' }}>{t('importedDetails.profile.source')}</label>
                    <div style={{ fontSize: '15px', color: "var(--app-neutral-ink, #333)" }}>{applicant?.source || t('importedDetails.profile.defaultSource')}</div>
                  </div>
                </div>

                {resolvedParsedData && (
                  <div style={{ marginBottom: '30px' }}>
                    <h4 style={{ fontSize: '16px', color: "var(--app-neutral-ink, #333)", marginBottom: '10px' }}>{t('importedDetails.profile.parsedSkills')}</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {resolvedParsedData.skills && resolvedParsedData.skills.length > 0 ? (
                        resolvedParsedData.skills.map((skill, i) => (
                          <span key={i} style={{ backgroundColor: '#f0f0f0', color: "var(--app-neutral-ink, #333)", padding: '4px 8px', borderRadius: '4px', fontSize: '13px' }}>
                            {skill}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: '#888', fontStyle: 'italic' }}>{t('importedDetails.profile.noSkills')}</span>
                      )}
                    </div>
                  </div>
                )}

                {applicant?.cvLink && (
                  <div style={{ marginTop: '20px' }}>
                    <iframe
                      src={applicant.cvLink}
                      style={{ width: '100%', height: '500px', border: '1px solid #ddd', borderRadius: '8px' }}
                      title={uiText("CV Preview")}
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'intelligence' && (
              <div>
                <h3 style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '10px', marginBottom: '20px', color: "var(--app-neutral-ink, #333)" }}>
                  {t('importedDetails.intelligence.title', 'Talent intelligence')}
                </h3>

                <IntelligencePanel
                  title={t('importedDetails.intelligence.recruiterSummary', 'Recruiter summary')}
                  subtitle={t('importedDetails.intelligence.recruiterSummarySubtitle', 'The clearest synthesized read on the candidate right now.')}
                  accent
                >
                  <div style={{ fontSize: '15px', color: '#23314d', lineHeight: '1.8' }}>
                    {recruiterSummary || t('importedDetails.intelligence.noSummary', 'No recruiter-grade summary has been produced yet. Reprocess the CV to enrich this profile.')}
                  </div>
                </IntelligencePanel>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', margin: '24px 0' }}>
                  <IntelligencePanel
                    title={t('importedDetails.intelligence.profileSignals', 'Profile')}
                    subtitle={t('importedDetails.intelligence.profileSignalsSubtitle', 'The standardized role, level, and operating context extracted from the CV.')}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '16px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.currentTitle', 'Current title')}</div>
                        <strong style={{ color: '#23314d' }}>{currentTitle || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.positionFamily', 'Position family')}</div>
                        <strong style={{ color: '#23314d' }}>{positionFamily || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.seniority', 'Seniority')}</div>
                        <strong style={{ color: '#23314d' }}>{resolvedAnalytics?.seniorityBucket || resolvedExtraction?.experience?.seniority || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.totalYears', 'Total experience')}</div>
                        <strong style={{ color: '#23314d' }}>{resolvedExtraction?.experience?.totalYears ? `${resolvedExtraction.experience.totalYears}y` : t('importedDetails.common.notAvailable')}</strong>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.department', 'Department')}</div>
                        <strong style={{ color: '#23314d' }}>{department || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.currentCompany', 'Current company')}</div>
                        <strong style={{ color: '#23314d' }}>{currentCompany || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.leadership', 'Leadership')}</div>
                        <strong style={{ color: '#23314d' }}>{leadershipReadiness || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                    </div>
                    {recruiterEvidenceItems.length > 0 && (
                      <div style={{ marginTop: '18px' }}>
                        <div style={{ fontSize: '12px', color: '#7a8699', marginBottom: '8px' }}>{t('importedDetails.intelligence.recruiterEvidence', 'Recruiter evidence')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {recruiterEvidenceItems.map((item) => (
                            <span key={item.label} style={{ backgroundColor: '#f4f6f8', color: '#445166', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>
                              {`${item.label}: ${item.value}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </IntelligencePanel>

                  <IntelligencePanel
                    title={t('importedDetails.intelligence.roleFit', 'Role fit')}
                    subtitle={t('importedDetails.intelligence.roleFitSubtitle', 'The strongest role directions implied by the extracted profile.')}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699', marginBottom: '8px' }}>{t('importedDetails.intelligence.targetRoles', 'Target roles')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {targetRoles.length > 0 ? targetRoles.map((item) => (
                            <span key={item} style={{ backgroundColor: '#eef5ff', color: COLORS.blue, padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: "var(--app-weight-strong, 700)" }}>
                              {item}
                            </span>
                          )) : <span style={{ color: '#7a8699', fontSize: '13px' }}>{t('importedDetails.intelligence.noTargetRoles', 'No target roles extracted yet.')}</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699', marginBottom: '8px' }}>{t('importedDetails.intelligence.roleArchetypes', 'Role archetypes')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {topRoleArchetypes.length > 0 ? topRoleArchetypes.map((item) => (
                            <span key={item} style={{ backgroundColor: '#eef5ff', color: COLORS.blue, padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: "var(--app-weight-strong, 700)" }}>
                              {item}
                            </span>
                          )) : <span style={{ color: '#7a8699', fontSize: '13px' }}>{t('importedDetails.intelligence.noArchetypes', 'No role archetypes extracted yet.')}</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699', marginBottom: '8px' }}>{t('importedDetails.intelligence.adjacentRoles', 'Adjacent roles')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {adjacentRoles.length > 0 ? adjacentRoles.map((item) => (
                            <span key={item} style={{ backgroundColor: '#f4f6f8', color: '#445166', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>
                              {item}
                            </span>
                          )) : <span style={{ color: '#7a8699', fontSize: '13px' }}>{t('importedDetails.intelligence.noAdjacentRoles', 'No adjacent roles have been standardized yet.')}</span>}
                        </div>
                      </div>
                    </div>
                  </IntelligencePanel>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                  <IntelligencePanel
                    title={t('importedDetails.intelligence.experienceScope', 'Experience and scope')}
                    subtitle={t('importedDetails.intelligence.experienceScopeSubtitle', 'The concrete trajectory, management, and scope signals extracted from the CV.')}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '16px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.peopleManagement', 'People management')}</div>
                        <strong style={{ color: '#23314d' }}>{peopleManagementScope || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.leadershipScope', 'Leadership scope')}</div>
                        <strong style={{ color: '#23314d' }}>{leadershipScope || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.budgetOwnership', 'Budget ownership')}</div>
                        <strong style={{ color: '#23314d' }}>{budgetOwnership || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.promotionPattern', 'Promotion pattern')}</div>
                        <strong style={{ color: '#23314d' }}>{promotionPattern || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.stabilitySignal', 'Stability signal')}</div>
                        <strong style={{ color: '#23314d' }}>{stabilitySignal || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.workModePreference', 'Work mode')}</div>
                        <strong style={{ color: '#23314d' }}>{workModePreference || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.profileShape', 'Profile shape')}</div>
                        <strong style={{ color: '#23314d' }}>{profileShape || t('importedDetails.common.notAvailable')}</strong>
                      </div>
                    </div>
                    {employmentGaps.length > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        <div style={{ fontSize: '12px', color: '#7a8699', marginBottom: '8px' }}>{t('importedDetails.intelligence.employmentGaps', 'Employment gaps')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {employmentGaps.map((gap) => (
                            <span key={gap} style={{ backgroundColor: '#f4f6f8', color: '#445166', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>
                              {gap}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </IntelligencePanel>

                  <IntelligencePanel
                    title={t('importedDetails.intelligence.specializationCredentials', 'Specialization and credentials')}
                    subtitle={t('importedDetails.intelligence.specializationCredentialsSubtitle', 'The areas, tools, and credentials already evidenced on the profile.')}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699', marginBottom: '8px' }}>{t('importedDetails.intelligence.domainExposure', 'Domain exposure')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {domainExposure.length > 0 ? domainExposure.map((item) => (
                            <span key={item} style={{ backgroundColor: '#f4f6f8', color: '#445166', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>
                              {item}
                            </span>
                          )) : <span style={{ color: '#7a8699', fontSize: '13px' }}>{t('importedDetails.intelligence.noDomainExposure', 'No domain exposure has been extracted yet.')}</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699', marginBottom: '8px' }}>{t('importedDetails.intelligence.skills', 'Skills')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {normalizedSkills.length > 0 ? normalizedSkills.slice(0, 12).map((item) => (
                            <span key={item} style={{ backgroundColor: '#f4f6f8', color: '#445166', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>
                              {item}
                            </span>
                          )) : <span style={{ color: '#7a8699', fontSize: '13px' }}>{t('importedDetails.intelligence.noSkills', 'No normalized skills extracted yet.')}</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699', marginBottom: '8px' }}>{t('importedDetails.intelligence.languages', 'Languages')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {languages.length > 0 ? languages.map((item) => (
                            <span key={item} style={{ backgroundColor: '#f4f6f8', color: '#445166', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>
                              {item}
                            </span>
                          )) : <span style={{ color: '#7a8699', fontSize: '13px' }}>{t('importedDetails.intelligence.noLanguages', 'No language coverage extracted yet.')}</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699', marginBottom: '8px' }}>{t('importedDetails.intelligence.certificationsMethodologies', 'Certifications and methodologies')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {[...certifications, ...methodologies].length > 0 ? [...certifications, ...methodologies].map((item) => (
                            <span key={item} style={{ backgroundColor: '#eef8f1', color: COLORS.green, padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: "var(--app-weight-strong, 700)" }}>
                              {item}
                            </span>
                          )) : <span style={{ color: '#7a8699', fontSize: '13px' }}>{t('importedDetails.intelligence.noCredentials', 'No certifications or methodologies extracted yet.')}</span>}
                        </div>
                      </div>
                      {educationItems.length > 0 && (
                        <div>
                          <div style={{ fontSize: '12px', color: '#7a8699', marginBottom: '8px' }}>{t('importedDetails.intelligence.education', 'Education')}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {educationItems.slice(0, 2).map((item, index) => (
                              <div key={`${item.institution || 'education'}_${index}`} style={{ color: '#23314d', fontSize: '13px', lineHeight: '1.6' }}>
                                <strong>{item.degree || t('importedDetails.common.notAvailable')}</strong>
                                {item.major ? ` • ${item.major}` : ''}
                                {item.institution ? ` • ${item.institution}` : ''}
                                {item.graduationYear ? ` • ${item.graduationYear}` : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </IntelligencePanel>
                </div>

                <IntelligencePanel
                  title={t('importedDetails.intelligence.nextActions', 'Actions')}
                  subtitle={t('importedDetails.intelligence.nextActionsSubtitle', 'The only follow-ups that still matter before this profile is fully usable for recruiters.')}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ fontSize: '12px', color: '#7a8699' }}>{t('importedDetails.intelligence.nextActions', 'Recommended next actions')}</div>
                      {recommendedNextActions.length > 0 ? recommendedNextActions.slice(0, 4).map((item, index) => (
                        <div key={`${item}_${index}`} style={{ color: '#23314d', fontSize: '13px', lineHeight: '1.7', display: 'flex', gap: '8px' }}>
                          <span style={{ color: COLORS.blue, fontWeight: "var(--app-weight-strong, 700)" }}>•</span>
                          <span>{item}</span>
                        </div>
                      )) : <span style={{ color: '#7a8699', fontSize: '13px' }}>{t('importedDetails.intelligence.noNextActions', 'No recommended next actions have been extracted yet.')}</span>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699', marginBottom: '8px' }}>{t('importedDetails.intelligence.missingRequired', 'Missing required fields')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {missingRequiredFields.length > 0 ? missingRequiredFields.map((item) => (
                            <span key={item} style={{ backgroundColor: '#fff4ea', color: 'var(--app-tone-a95f1e, #a95f1e)', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>
                              {item}
                            </span>
                          )) : <span style={{ color: '#6b778c', fontSize: '13px' }}>{t('importedDetails.intelligence.noMissingRequired', 'No missing required fields are currently flagged.')}</span>}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7a8699', marginBottom: '8px' }}>{t('importedDetails.intelligence.lowConfidence', 'Low-confidence fields')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {lowConfidenceFields.length > 0 ? lowConfidenceFields.map((item) => (
                            <span key={item} style={{ backgroundColor: '#f4f6f8', color: '#445166', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>
                              {item}
                            </span>
                          )) : <span style={{ color: '#6b778c', fontSize: '13px' }}>{t('importedDetails.intelligence.noLowConfidence', 'No low-confidence fields are currently flagged.')}</span>}
                        </div>
                      </div>
                      {(missingContactData.length > 0 || missingEvaluationData.length > 0) && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {missingContactData.length > 0 && (
                            <div style={{ color: '#6b778c', fontSize: '13px', lineHeight: '1.6' }}>
                              <strong style={{ color: '#23314d' }}>{t('importedDetails.intelligence.contactFollowUp', 'Contact follow-up')}:</strong> {missingContactData.join(', ')}
                            </div>
                          )}
                          {missingEvaluationData.length > 0 && (
                            <div style={{ color: '#6b778c', fontSize: '13px', lineHeight: '1.6' }}>
                              <strong style={{ color: '#23314d' }}>{t('importedDetails.intelligence.evaluationFollowUp', 'Evaluation follow-up')}:</strong> {missingEvaluationData.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </IntelligencePanel>
              </div>
            )}

            {activeTab === 'analysis' && analysis && (
              <div>
                <h3 style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '10px', marginBottom: '20px', color: "var(--app-neutral-ink, #333)" }}>
                  {t('importedDetails.analysis.title')}
                </h3>
                <div style={{ lineHeight: '1.6', color: "var(--app-neutral-ink, #333)", backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px' }}>
                  {typeof analysis === 'string' ? renderFormattedAnalysis(analysis) : (
                    <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(analysis, null, 2)}</pre>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div>
                <h3 style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '10px', marginBottom: '20px', color: "var(--app-neutral-ink, #333)" }}>
                  {t('importedDetails.history.title')}
                </h3>

                {loadingHistory ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: isRTL ? 0 : '10px', marginLeft: isRTL ? '10px' : 0 }}></i>
                    {t('importedDetails.history.loading')}
                  </div>
                ) : history.length > 0 ? (
                  <div>
                    {history.map((item, index) => renderHistoryItem(item, index))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#888', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                    <i className="fa-solid fa-history" style={{ fontSize: '32px', marginBottom: '15px', color: 'var(--app-text, #ddd)' }}></i>
                    <p>{t('importedDetails.history.empty')}</p>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportedApplicantDetailsPopup;
